import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon
} from '@mui/icons-material';
import QRCode from 'qrcode';

interface Device {
  id: string;
  device_id: string;
  device_name: string;
  registered_at?: string;
  last_seen: string;
  is_active: boolean;
  mdm_group?: string;
  location?: string;
  current_template_id?: string;
}

interface FormTemplate {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
}

const Devices: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [deviceName] = useState('iPad');
  const [generatingToken, setGeneratingToken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationToken, setRegistrationToken] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [registeredDeviceName, setRegisteredDeviceName] = useState<string>('');
  const [hasShownSuccess, setHasShownSuccess] = useState(false);
  const [updatingTemplate, setUpdatingTemplate] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${supabaseUrl}/rest/v1/form_templates?select=id,name,description,is_active&is_active=eq.true&order=name.asc`, {
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }

      const data = await response.json();
      setTemplates(data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, [supabaseUrl, supabaseAnonKey]);

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${supabaseUrl}/rest/v1/devices?select=*&order=registered_at.desc`, {
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch devices');
      }

      const data = await response.json();
      setDevices(data);
    } catch (err) {
      setError('Failed to load devices');
      console.error('Error fetching devices:', err);
    } finally {
      setLoading(false);
    }
  }, [supabaseUrl, supabaseAnonKey]);

  const checkForNewRegistration = async (token: string) => {
    if (!token) return;

    try {
      const response = await fetch(`${supabaseUrl}/rest/v1/devices?registration_token=eq.${token}&select=device_name,device_id,registered_at`, {
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const device = data[0];
          
          // Check if device ID is no longer the temporary one (meaning it was registered)
          if (!device.device_id.startsWith('temp_') && !hasShownSuccess) {
            // Stop polling immediately
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            
            // Device has been registered!
            setRegisteredDeviceName(device.device_name);
            setShowSuccessMessage(true);
            setQrDialogOpen(false);
            setHasShownSuccess(true);
            
            // Refresh devices list
            fetchDevices();
          }
        }
      }
    } catch (err) {
      console.error('Error checking registration status:', err);
    }
  };

  const generateRegistrationToken = async () => {
    try {
      setGeneratingToken(true);
      setError(null);
      setShowSuccessMessage(false);
      setHasShownSuccess(false);

      const response = await fetch(`${supabaseUrl}/functions/v1/generate-registration-token`, {
        method: 'POST',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ deviceName })
      });

      if (!response.ok) {
        throw new Error('Failed to generate registration token');
      }

      const data = await response.json();
      setRegistrationToken(data.registrationToken);
      
      // Generate QR code with registration data
      const qrData = JSON.stringify({
        registrationToken: data.registrationToken,
        adminPanelUrl: window.location.origin,
        deviceName: data.deviceName
      });

      const qrCodeDataUrl = await QRCode.toDataURL(qrData, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      setQrCodeUrl(qrCodeDataUrl);
      setQrDialogOpen(true);
      
      // Start polling for registration
      const interval = setInterval(() => {
        checkForNewRegistration(data.registrationToken);
      }, 2000); // Check every 2 seconds
      pollingIntervalRef.current = interval;
    } catch (err) {
      setError('Failed to generate registration token');
      console.error('Error generating token:', err);
    } finally {
      setGeneratingToken(false);
    }
  };

  const updateDeviceTemplate = async (deviceId: string, templateId: string | null) => {
    try {
      setUpdatingTemplate(deviceId);
      
      const response = await fetch(`${supabaseUrl}/rest/v1/devices?id=eq.${deviceId}`, {
        method: 'PATCH',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_template_id: templateId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update device template');
      }

      // Refresh devices list
      fetchDevices();
    } catch (err) {
      setError('Failed to update device template');
      console.error('Error updating device template:', err);
    } finally {
      setUpdatingTemplate(null);
    }
  };

  const handleDeleteClick = (device: Device) => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDevice = async () => {
    if (!deviceToDelete) return;
    
    try {
      const response = await fetch(`${supabaseUrl}/rest/v1/devices?id=eq.${deviceToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete device');
      }

      // Refresh devices list
      fetchDevices();
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
    } catch (err) {
      setError('Failed to delete device');
      console.error('Error deleting device:', err);
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };


  const getTemplateName = (templateId: string | undefined) => {
    if (!templateId) return 'No template assigned';
    const template = templates.find(t => t.id === templateId);
    return template ? template.name : 'Unknown template';
  };

  useEffect(() => {
    fetchDevices();
    fetchTemplates();
  }, [fetchDevices, fetchTemplates]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Device Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={generateRegistrationToken}
          disabled={generatingToken}
        >
          {generatingToken ? <CircularProgress size={20} /> : 'Register New Device'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Registered Devices ({devices.length})
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : devices.length === 0 ? (
            <Typography variant="body1" color="text.secondary" textAlign="center" p={3}>
              No devices registered yet. Click "Register New Device" to get started.
            </Typography>
          ) : (
            <List>
              {devices.map((device) => (
                <ListItem key={device.id} divider>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6">{device.device_name}</Typography>
                        <Chip
                          label={getTemplateName(device.current_template_id)}
                          color={device.current_template_id ? "primary" : "default"}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Device ID: {device.device_id}
                        </Typography>
                        {device.registered_at && (
                          <Typography variant="body2" color="text.secondary">
                            Registered: {formatDate(device.registered_at)}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Last Seen: {formatDate(device.last_seen)}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box display="flex" alignItems="center" gap={1}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Template</InputLabel>
                        <Select
                          value={device.current_template_id || ''}
                          onChange={(e) => updateDeviceTemplate(device.id, e.target.value || null)}
                          disabled={updatingTemplate === device.id}
                          label="Template"
                        >
                          <MenuItem value="">
                            <em>No template</em>
                          </MenuItem>
                          {templates.map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                              {template.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteClick(device)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Register New Device</DialogTitle>
        <DialogContent>
          <Box textAlign="center" p={2}>
            <Typography variant="body1" gutterBottom>
              Scan this QR code with your iPad to register it:
            </Typography>
            {qrCodeUrl && (
              <Box mt={2}>
                <img src={qrCodeUrl} alt="Registration QR Code" style={{ maxWidth: '100%', height: 'auto' }} />
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" mt={2}>
              The QR code will expire in 10 minutes for security.
            </Typography>
            <Typography variant="body2" color="primary" mt={1}>
              Waiting for device registration...
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setQrDialogOpen(false);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Success Message Dialog */}
      <Dialog open={showSuccessMessage} onClose={() => setShowSuccessMessage(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Box color="success.main">✓</Box>
            Device Registered Successfully!
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box textAlign="center" p={2}>
            <Typography variant="h6" gutterBottom>
              {registeredDeviceName} has been registered successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The device is now ready to use and will appear in the devices list below.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowSuccessMessage(false)} 
            variant="contained" 
            color="primary"
            fullWidth
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Device Deletion</DialogTitle>
        <DialogContent>
          <Box textAlign="center" p={2}>
            <Typography variant="h6" gutterBottom>
              Are you sure you want to delete this device?
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Device: <strong>{deviceToDelete?.device_name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Device ID: {deviceToDelete?.device_id}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action cannot be undone. The device will need to be re-registered to use the system again.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDeleteDevice} 
            variant="contained" 
            color="error"
          >
            Delete Device
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Devices;
